/** Pilcore Purchasing — inventory count sessions. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiArrowDown, FiArrowUp, FiEye, FiPlay, FiPlus, FiRotateCcw, FiSearch, FiTrash2, FiX } from "react-icons/fi";
import axios from "../../../api/axiosInstance";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import AddInventoryCountPopup from "../popups/AddInventoryCountPopup";
import InventoryOpeningBalancePopup from "../popups/InventoryOpeningBalancePopup";
import useInventoryCountAccess from "../useInventoryCountAccess";
import PurchasingDocumentDialog from "../popups/PurchasingDocumentDialog";
import { getInventoryCountListMessages } from "./InventoryCountTab.messages";
import { getLocationName } from "../../utils/locationNames";
import "../../PurchasingUi.css";
import "./InventoryCountTab.css";

const STATUSES = ["open", "in_progress", "finalized", "reverted", "cancelled"];
const normalize = (value) => String(value ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const statusKey = (value) => normalize(value).trim().replace(/\s+/g, "_") || "unknown";

function displayDate(value, locale) {
  if (!value) return "—";
  const date = new Date(String(value).slice(0, 10) + "T12:00:00");
  return Number.isNaN(date.getTime()) ? "—" : new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", year: "numeric" }).format(date);
}

function CountLocations({ locations, words }) {
  if (!locations.length) return <span className="pu-counts-muted">{words.noLocations}</span>;
  return <div className="pu-counts-locations">
    {locations.slice(0, 2).map((location) => <span key={location.id} className="pu-counts-location">{location.label}</span>)}
    {locations.length > 2 && <details className="pu-counts-location-details">
      <summary aria-label={words.moreLocations(locations.length - 2)}>+{locations.length - 2}</summary>
      <ul>{locations.slice(2).map((location) => <li key={location.id}>{location.label}</li>)}</ul>
    </details>}
  </div>;
}

export default function InventoryCountTab({ businessUnitId }) {
  const { themeId, languageId } = usePmsTheme();
  const words = getInventoryCountListMessages(languageId);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [notice, setNotice] = useState("");
  const [popup, setPopup] = useState(null);
  const [openingPopup, setOpeningPopup] = useState(false);
  const { can, requestConfig, contextKey } = useInventoryCountAccess(businessUnitId);
  const canView = can("view");
  const canValidateOpening = can("validate_opening");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sort, setSort] = useState({ key: "id", direction: "desc" });
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [actionError, setActionError] = useState("");
  const requestId = useRef(0);
  const scopeVersion = useRef(0);
  const actionPending = useRef(false);

  const fetchSessions = useCallback(async ({ quiet = false } = {}) => {
    if (!can("view") || !can("view", true)) return;
    const request = ++requestId.current;
    if (quiet) setRefreshing(true); else setLoading(true);
    try {
      const response = await axios.get("/api/purchasing/inventory-counts", requestConfig());
      if (request !== requestId.current) return;
      if (!Array.isArray(response.data)) {
        setLoadError({ status: response.status, unexpectedResponse: true });
        return;
      }
      setSessions(response.data); setLoaded(true); setLoadError(null);
    } catch (error) {
      if (request === requestId.current) setLoadError({ status: error?.response?.status });
    } finally {
      if (request === requestId.current) { setLoading(false); setRefreshing(false); }
    }
  }, [can, requestConfig]);

  useEffect(() => {
    scopeVersion.current += 1;
    actionPending.current = false;
    setLoaded(false); setSessions([]); setPopup(null); setOpeningPopup(false); setConfirmAction(null); setConfirmBusy(false);
    setNotice(""); setActionError(""); setLoadError(null); setSearch(""); setStatusFilter("all");
    fetchSessions();
    return () => { requestId.current += 1; scopeVersion.current += 1; };
  }, [businessUnitId, contextKey, fetchSessions]);

  useEffect(() => {
    const onFocus = () => { if (!popup && !openingPopup && !confirmAction && !loading) fetchSessions({ quiet: true }); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [popup, openingPopup, confirmAction, loading, fetchSessions]);

  const rows = useMemo(() => sessions.map((session) => {
    const status = statusKey(session.status);
    const mode = session.blind_mode === true || session.blind_mode === 1 || session.blind_mode === "true" || session.blind_mode === "1" ? "blind" : "visible";
    const locations = (Array.isArray(session.locations) ? session.locations : []).filter(Boolean).map((location, index) => ({
      id: typeof location === "object" ? location.id ?? index : location,
      label: getLocationName(location, languageId),
    }));
    const code = `IC-${session.id}`;
    const creator = session.creator?.name || session.created_by || "—";
    const date = displayDate(session.snapshot_date, words.locale);
    const count = Number(session.lines_count ?? session.line_count ?? session.total_lines ?? session.lines?.length ?? 0);
    return {
      id: session.id, code, date, rawDate: String(session.snapshot_date || ""), creator, mode, locations,
      lines: Number.isFinite(count) ? count : 0, status,
      statusLabel: words.status[status] || session.status || words.unknown,
      canContinue: ["open", "in_progress", "reverted"].includes(status),
      search: normalize([code, session.snapshot_date, date, creator, words.mode[mode], words.status[status], session.status, ...locations.map((location) => location.label)].join(" ")),
    };
  }), [sessions, words, languageId]);

  const visibleRows = useMemo(() => rows.filter((row) => (statusFilter === "all" || row.status === statusFilter) && row.search.includes(normalize(search.trim()))).sort((left, right) => {
    const comparison = sort.key === "date" ? left.rawDate.localeCompare(right.rawDate) : Number(left.id) - Number(right.id);
    return (comparison || Number(left.id) - Number(right.id)) * (sort.direction === "asc" ? 1 : -1);
  }), [rows, search, statusFilter, sort]);
  const counts = rows.reduce((result, row) => ({ ...result, [row.status]: (result[row.status] || 0) + 1 }), {});
  const locked = refreshing || loading || Boolean(loadError) || !loaded || confirmBusy;
  const openSession = (id = null, autoPromptFinalize = false) => {
    if (!locked && can(id ? "view" : "create", true) && (!autoPromptFinalize || can("finalize", true))) { setNotice(""); setPopup({ id, autoPromptFinalize }); }
  };
  const changeSort = (key) => setSort((current) => ({ key, direction: current.key === key && current.direction === "desc" ? "asc" : "desc" }));
  const sortHeader = (key, label) => <th scope="col" aria-sort={sort.key === key ? sort.direction === "asc" ? "ascending" : "descending" : "none"}>
    <button type="button" className="pu-counts-sort" onClick={() => changeSort(key)}>{label}{sort.key === key && (sort.direction === "asc" ? <FiArrowUp aria-hidden="true" /> : <FiArrowDown aria-hidden="true" />)}</button>
  </th>;

  const handleConfirm = async () => {
    if (!confirmAction || locked || actionPending.current || !can(confirmAction.type, true)) return;
    actionPending.current = true; setConfirmBusy(true); setActionError("");
    const scope = scopeVersion.current;
    try {
      if (confirmAction.type === "delete") await axios.delete(`/api/purchasing/inventory-counts/${confirmAction.id}`, requestConfig("delete"));
      else if (confirmAction.type === "revert") await axios.post(`/api/purchasing/inventory-counts/${confirmAction.id}/revert`, {}, requestConfig("revert"));
      if (scope !== scopeVersion.current) return;
      setNotice(confirmAction.type === "delete" ? "deleted" : "reverted"); setConfirmAction(null);
      await fetchSessions({ quiet: true });
    } catch (error) {
      if (scope === scopeVersion.current) setActionError(error.response?.data?.error || error.response?.data?.message || "actionError");
    } finally {
      if (scope === scopeVersion.current) { actionPending.current = false; setConfirmBusy(false); }
    }
  };
  const requestAction = (type, row) => { if (!locked && can(type, true)) { setActionError(""); setNotice(""); setConfirmAction({ type, id: row.id, code: row.code }); } };

  if (!canView) return <p role="alert">{languageId === "es" ? "No tienes permiso para consultar los recuentos de inventario." : "You do not have permission to view inventory counts."}</p>;

  return <section className="pilcore-purchasing-ui pu-counts" data-theme={themeId} aria-labelledby="pu-counts-title" aria-busy={loading || refreshing}>
    <header className="pu-counts-header">
      <div><h2 id="pu-counts-title">{words.title}</h2><p>{words.description}</p></div>
      <div className="pu-counts-header-actions">
        {canValidateOpening && <button type="button" className="pu-counts-button" onClick={() => setOpeningPopup(true)} disabled={locked}>
          {languageId === "es" ? "Importar saldo inicial" : "Import opening balance"}
        </button>}
        {can("create") && <button type="button" className="pu-counts-button pu-counts-button--primary" onClick={() => openSession()} disabled={locked}><FiPlus aria-hidden="true" />{words.start}</button>}
      </div>
    </header>
    {loadError && <div className="pu-counts-notice pu-counts-notice--error" role="alert"><span>{words.loadError}
      {loadError.unexpectedResponse && ` ${words.unexpectedResponse}`}
      {Number.isInteger(loadError.status) && ` (HTTP ${loadError.status})`}</span>
      <button type="button" className="pu-counts-button" onClick={() => fetchSessions({ quiet: loaded })} disabled={loading || refreshing}>{words.retry}</button>
    </div>}
    {notice && <p className="pu-counts-notice pu-counts-notice--success" role="status">{words[notice]}</p>}
    {loaded && <div className="pu-counts-toolbar">
      <label className="pu-counts-search"><FiSearch aria-hidden="true" /><input className="form-control" type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={words.search} aria-label={words.search} /></label>
      <label className="pu-counts-filter"><span>{words.statusLabel}</span><select className="form-select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
        <option value="all">{words.all} ({rows.length})</option>
        {STATUSES.map((status) => <option key={status} value={status}>{words.status[status]} ({counts[status] || 0})</option>)}
      </select></label>
      {(search || statusFilter !== "all") && <button type="button" className="pu-counts-button pu-counts-icon" onClick={() => { setSearch(""); setStatusFilter("all"); }} title={words.clear} aria-label={words.clear}><FiX aria-hidden="true" /></button>}
      <span className="pu-counts-count">{refreshing ? words.syncing : words.count(visibleRows.length, rows.length)}</span>
    </div>}
    {loading && !loaded ? <p className="pu-counts-empty" role="status">{words.loading}</p> : loaded && <div className="pu-counts-table-scroll" tabIndex={0} aria-label={words.title}>
      <table className="pu-counts-table"><caption className="pu-counts-sr-only">{words.title}</caption>
        <colgroup><col className="pu-counts-col-code" /><col className="pu-counts-col-date" /><col className="pu-counts-col-mode" /><col className="pu-counts-col-status" /><col className="pu-counts-col-creator" /><col className="pu-counts-col-lines" /><col className="pu-counts-col-locations" /><col className="pu-counts-col-actions" /></colgroup>
        <thead><tr>{sortHeader("id", words.session)}{sortHeader("date", words.date)}<th scope="col">{words.modeLabel}</th><th scope="col">{words.statusLabel}</th><th scope="col">{words.creator}</th><th scope="col" className="pu-counts-number">{words.lines}</th><th scope="col">{words.locations}</th><th scope="col">{words.actions}</th></tr></thead>
        <tbody>{visibleRows.map((row) => <tr key={row.id}>
          <th scope="row"><button type="button" className="pu-counts-code" disabled={locked} onClick={() => openSession(row.id)}>{row.code}</button></th>
          <td className="pu-counts-date">{row.date}</td><td><span className="pu-counts-mode" title={words.modeHelp[row.mode]}>{words.mode[row.mode]}</span></td>
          <td><span className={`pu-counts-status pu-counts-status--${STATUSES.includes(row.status) ? row.status : "unknown"}`}>{row.statusLabel}</span></td>
          <td>{row.creator}</td><td className="pu-counts-number">{row.lines.toLocaleString(words.locale)}</td><td><CountLocations locations={row.locations} words={words} /></td>
          <td><div className="pu-counts-actions">
            <button type="button" className="pu-counts-button pu-counts-button--continue" disabled={locked} onClick={() => openSession(row.id)}>{row.canContinue && can("update") ? <FiPlay aria-hidden="true" /> : <FiEye aria-hidden="true" />}{row.canContinue && can("update") ? words.continue : words.review}</button>
            {row.canContinue && can("finalize") && <button type="button" className="pu-counts-button" disabled={locked} onClick={() => openSession(row.id, true)}>{words.finalize}</button>}
            {row.status === "open" && can("delete") && <button type="button" className="pu-counts-button pu-counts-icon pu-counts-button--danger" disabled={locked} onClick={() => requestAction("delete", row)} title={words.delete} aria-label={`${words.delete}: ${row.code}`}><FiTrash2 aria-hidden="true" /></button>}
            {row.status === "finalized" && can("revert") && <button type="button" className="pu-counts-button pu-counts-button--danger" disabled={locked} onClick={() => requestAction("revert", row)} title={words.revert} aria-label={`${words.revert}: ${row.code}`}><FiRotateCcw aria-hidden="true" />{words.revertShort}</button>}
          </div></td>
        </tr>)}
        {!visibleRows.length && <tr><td colSpan={8} className="pu-counts-empty"><strong>{rows.length ? words.noMatches : words.empty}</strong>{!rows.length && <p>{words.emptyHelp}</p>}</td></tr>}
        </tbody>
      </table>
    </div>}
    {popup && <AddInventoryCountPopup isOpen sessionIdFromList={popup.id} businessUnitId={businessUnitId} autoPromptFinalize={popup.autoPromptFinalize} pilcoreAppearance onClose={() => { setPopup(null); fetchSessions({ quiet: true }); }} />}
    {openingPopup && canValidateOpening && <InventoryOpeningBalancePopup isOpen businessUnitId={businessUnitId}
      onClose={() => setOpeningPopup(false)} onValidated={() => fetchSessions({ quiet: true })} />}
    <PurchasingDocumentDialog isOpen={Boolean(confirmAction)} title={confirmAction?.type === "delete" ? words.deleteTitle : words.revertTitle}
      message={<><strong>{confirmAction?.code}</strong><p>{confirmAction?.type === "delete" ? words.deleteWarning : words.revertWarning}</p>{actionError && <p className="pu-counts-dialog-error" role="alert">{words.actionError}{actionError !== "actionError" && <span> {actionError}</span>}</p>}</>}
      confirmLabel={confirmBusy ? words.working : confirmAction?.type === "delete" ? words.delete : words.revert} cancelLabel={words.cancel}
      tone="warning" isBusy={confirmBusy} onConfirm={handleConfirm} onClose={() => { if (!actionPending.current) setConfirmAction(null); }} />
  </section>;
}
